import React, { useState, useRef, useCallback } from "react";
import { Users, Upload, Search, Download, AlertTriangle, ChevronDown, ChevronUp, X, Clock } from "lucide-react";
import "../../styles/sidebar/ListAnalysis.css";
import { useMamdani } from "../../context/MamdaniContext";

const API_BASE = "https://api.anti-bot.ai";

const BOT_TYPE_CONFIG = {
  BOT:        { label: "בוט",        color: "#C14B56", bg: "rgba(193,75,86,0.15)" },
  LIKELY_BOT: { label: "כנראה בוט",  color: "#D4895A", bg: "rgba(212,137,90,0.15)" },
  SUSPICIOUS: { label: "חשוד",        color: "#C8B455", bg: "rgba(200,180,85,0.15)" },
  HUMAN:      { label: "אנושי",       color: "#52C26D", bg: "rgba(82,194,109,0.15)" },
};

const BOT_TYPE_CONFIG_EN = {
  BOT:        { label: "Bot",         color: "#C14B56", bg: "rgba(193,75,86,0.15)" },
  LIKELY_BOT: { label: "Likely Bot",  color: "#D4895A", bg: "rgba(212,137,90,0.15)" },
  SUSPICIOUS: { label: "Suspicious",  color: "#C8B455", bg: "rgba(200,180,85,0.15)" },
  HUMAN:      { label: "Human",       color: "#52C26D", bg: "rgba(82,194,109,0.15)" },
};

function ScoreBadge({ score }) {
  let color = "#52C26D";
  if (score >= 75) color = "#C14B56";
  else if (score >= 50) color = "#D4895A";
  else if (score >= 25) color = "#C8B455";

  const circumference = 2 * Math.PI * 14;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="la-score-badge">
      <svg width="36" height="36" viewBox="0 0 36 36">
        <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
        <circle
          cx="18" cy="18" r="14" fill="none"
          stroke={color} strokeWidth="3"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 18 18)"
        />
        <text x="18" y="22" textAnchor="middle" fill={color} fontSize="9" fontWeight="700">
          {score}
        </text>
      </svg>
    </div>
  );
}

const NEEDS_AI = new Set(['BOT', 'LIKELY_BOT', 'SUSPICIOUS']);

function ResultRow({ result, index }) {
  const { mamdaniMode: mm } = useMamdani();
  const [expanded, setExpanded]   = useState(false);
  const [aiText, setAiText]       = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const cfgMap = mm ? BOT_TYPE_CONFIG_EN : BOT_TYPE_CONFIG;
  const cfg = cfgMap[result.bot_type] || cfgMap.HUMAN;

  const age = result.account_age_days != null
    ? result.account_age_days < 365
      ? `${result.account_age_days} ${mm ? "days" : "ימים"}`
      : `${Math.floor(result.account_age_days / 365)} ${mm ? "yr" : "שנ'"}`
    : "—";

  const fetchAI = async (e) => {
    e.stopPropagation();
    if (aiText || aiLoading) return;
    setAiLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/api/explain-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username:        result.username,
          bot_score:       result.bot_score,
          followers:       result.followers,
          following:       result.following,
          tweet_count:     result.tweet_count,
          description:     result.description,
          account_age_days: result.account_age_days,
        }),
      });
      const data = await resp.json();
      setAiText(data.success ? data.explanation : (mm ? "Error fetching explanation." : "שגיאה בקבלת הסבר."));
    } catch {
      setAiText(mm ? "Network error." : "שגיאת רשת.");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <>
      <tr
        className={`la-row ${result.is_bot ? "la-row-bot" : ""}`}
        onClick={() => setExpanded(e => !e)}
      >
        <td className="la-td la-td-num">{index + 1}</td>
        <td className="la-td la-td-user">
          <div className="la-user-cell">
            {result.profile_image ? (
              <img src={result.profile_image} alt="" className="la-avatar" />
            ) : (
              <div className="la-avatar la-avatar-placeholder">
                {(result.username || "?")[0].toUpperCase()}
              </div>
            )}
            <div>
              <div className="la-username">@{result.username}</div>
              <div className="la-name">{result.name}</div>
            </div>
          </div>
        </td>
        <td className="la-td la-td-center">{result.followers?.toLocaleString()}</td>
        <td className="la-td la-td-center">{result.following?.toLocaleString()}</td>
        <td className="la-td la-td-center">{age}</td>
        <td className="la-td la-td-center">{result.tweet_count?.toLocaleString()}</td>
        <td className="la-td la-td-center">
          <ScoreBadge score={result.bot_score} />
        </td>
        <td className="la-td la-td-center">
          <span className="la-type-badge" style={{ color: cfg.color, background: cfg.bg }}>
            {cfg.label}
          </span>
        </td>
        <td className="la-td la-td-center">
          {expanded ? <ChevronUp size={14} color="#8A7A6E" /> : <ChevronDown size={14} color="#8A7A6E" />}
        </td>
      </tr>
      {expanded && (
        <tr className="la-expand-row">
          <td colSpan={9}>
            <div className="la-expand-body">
              <div className="la-expand-grid">
                <div className="la-expand-item">
                  <span className="la-expand-label">{mm ? "Verified" : "מאומת"}</span>
                  <span className="la-expand-val">{result.verified ? "✓" : "✗"}</span>
                </div>
                <div className="la-expand-item">
                  <span className="la-expand-label">{mm ? "Location" : "מיקום"}</span>
                  <span className="la-expand-val">{result.location || "—"}</span>
                </div>
                <div className="la-expand-item la-expand-bio">
                  <span className="la-expand-label">{mm ? "Bio" : "ביו"}</span>
                  <span className="la-expand-val">{result.description || (mm ? "No description" : "אין תיאור")}</span>
                </div>
                <div className="la-expand-item">
                  <span className="la-expand-label">{mm ? "X Profile" : "פרופיל X"}</span>
                  <a
                    href={`https://x.com/${result.username}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="la-expand-link"
                    onClick={e => e.stopPropagation()}
                  >
                    x.com/{result.username}
                  </a>
                </div>
              </div>

              {NEEDS_AI.has(result.bot_type) && (
                <div className="la-ai-section">
                  {!aiText && !aiLoading && (
                    <button className="la-ai-btn" onClick={fetchAI}>
                      <span className="la-ai-icon">✦</span>
                      {mm ? "AI Explain" : "הסבר AI"}
                    </button>
                  )}
                  {aiLoading && (
                    <div className="la-ai-loading">
                      <div className="la-spinner la-spinner-sm" />
                      <span>{mm ? "Analyzing with AI..." : "מנתח עם AI..."}</span>
                    </div>
                  )}
                  {aiText && (
                    <div className="la-ai-result">
                      <span className="la-ai-label">✦ {mm ? "AI Analysis" : "ניתוח AI"}</span>
                      <p className="la-ai-text">{aiText}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function ListAnalysis() {
  const { mamdaniMode: mm } = useMamdani();
  const [text, setText]               = useState("");
  const [loading, setLoading]         = useState(false);
  const [streamResults, setStreamResults] = useState([]);
  const [summary, setSummary]         = useState(null);
  const [errors, setErrors]           = useState([]);
  const [progress, setProgress]       = useState(null);
  const [rateLimitWait, setRateLimitWait] = useState(null);
  const [filter, setFilter]           = useState("all");
  const [fileLoading, setFileLoading] = useState(false);
  const [isDragOver, setIsDragOver]   = useState(false);
  const fileRef = useRef();

  const parseUsernames = (raw) =>
    raw
      .split(/[\n,;]+/)
      .map(s => s.trim().replace(/^@/, "").replace(/https?:\/\/(twitter|x)\.com\//i, ""))
      .filter(s => /^[A-Za-z0-9_]{1,15}$/.test(s));

  const processFile = async (file) => {
    const name = file.name.toLowerCase();
    if (name.endsWith(".pdf")) {
      setFileLoading(true);
      try {
        const formData = new FormData();
        formData.append("file", file);
        const resp = await fetch(`${API_BASE}/api/parse-upload`, { method: "POST", body: formData });
        const data = await resp.json();
        if (data.success) setText(data.usernames.join("\n"));
        else alert((mm ? "PDF parse error: " : "שגיאה בניתוח PDF: ") + data.error);
      } catch (err) {
        alert((mm ? "Network error: " : "שגיאת רשת: ") + err.message);
      } finally {
        setFileLoading(false);
      }
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => setText(parseUsernames(ev.target.result).join("\n"));
      reader.readAsText(file);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    await processFile(file);
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = () => setIsDragOver(false);

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragOver(false);

    // גרירת כרטיס דוגמה
    if (e.dataTransfer.getData("text/plain") === "sample") {
      setFileLoading(true);
      try {
        const resp = await fetch(`${API_BASE}/api/sample-file`);
        const text = await resp.text();
        setText(parseUsernames(text).join("\n"));
      } catch (err) {
        alert("שגיאה בטעינת קובץ דוגמה");
      } finally {
        setFileLoading(false);
      }
      return;
    }

    // גרירת קובץ אמיתי מהמחשב
    const file = e.dataTransfer.files[0];
    if (file) await processFile(file);
  };

  const handleAnalyze = useCallback(async () => {
    const usernames = parseUsernames(text);
    if (!usernames.length) return;

    setLoading(true);
    setStreamResults([]);
    setSummary(null);
    setErrors([]);
    setRateLimitWait(null);
    setProgress({ current: 0, total: usernames.length });

    let allResults = [];

    try {
      const resp = await fetch(`${API_BASE}/api/analyze-list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ usernames }),
      });

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // parse complete SSE events (separated by \n\n)
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";

        for (const chunk of chunks) {
          if (!chunk.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(chunk.slice(6));

            if (event.type === "batch") {
              setProgress({ current: event.processed, total: event.total });
              allResults = [...allResults, ...event.results];
              setStreamResults([...allResults]);
              setRateLimitWait(null);
            } else if (event.type === "rate_limit") {
              setRateLimitWait(event.wait);
              setProgress(p => p ? { ...p, waiting: true } : p);
            } else if (event.type === "done") {
              setSummary(event.summary);
              setErrors(event.errors || []);
            }
          } catch (_) {}
        }
      }
    } catch (err) {
      setErrors([{ username: "—", error: (mm ? "Network error: " : "שגיאת רשת: ") + err.message }]);
    } finally {
      setLoading(false);
      setProgress(null);
      setRateLimitWait(null);
    }
  }, [text]);

  const handleExport = () => {
    if (!streamResults.length) return;
    const rows = [
      ["שם משתמש", "שם", "עוקבים", "עוקב אחרי", "ציוצים", "גיל חשבון (ימים)", "ציון בוט", "סיווג", "מאומת"],
      ...streamResults.map(r => [
        r.username, r.name, r.followers, r.following,
        r.tweet_count, r.account_age_days ?? "",
        r.bot_score, BOT_TYPE_CONFIG[r.bot_type]?.label || r.bot_type,
        r.verified ? "כן" : "לא",
      ])
    ];
    const csv = "﻿" + rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `antibot_list_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const usernames = parseUsernames(text);

  const activeSummary = summary || (streamResults.length > 0 ? {
    total: streamResults.length,
    bots: streamResults.filter(r => r.bot_type === "BOT" || r.bot_type === "LIKELY_BOT").length,
    suspicious: streamResults.filter(r => r.bot_type === "SUSPICIOUS").length,
    humans: streamResults.filter(r => r.bot_type === "HUMAN").length,
    errors: errors.length,
  } : null);

  const filtered = filter === "all"       ? streamResults
    : filter === "bots"      ? streamResults.filter(r => r.bot_type === "BOT" || r.bot_type === "LIKELY_BOT")
    : filter === "suspicious" ? streamResults.filter(r => r.bot_type === "SUSPICIOUS")
    : streamResults.filter(r => r.bot_type === "HUMAN");

  const progressPct = progress ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <div className="la-wrapper" dir={mm ? "ltr" : "rtl"}>

      <div className="la-header">
        <div className="la-header-left">
          <Users size={18} color="#D89B5A" />
          <span className="la-title">{mm ? "List Analysis" : "ניתוח רשימות"}</span>
        </div>
        <div className="la-header-sub">
          {mm ? "Real-time X/Twitter account analysis for bot detection" : "ניתוח חשבונות X/טוויטר לזיהוי בוטים בזמן אמת"}
        </div>
      </div>

      {/* Input card */}
      <div className="la-card">
        <div className="la-card-top">

          {/* אזור גרירה + textarea */}
          <div
            className={`la-textarea-wrap ${isDragOver ? "la-drop-active" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <textarea
              className="la-textarea"
              placeholder={mm
                ? "Enter usernames — one per line\nor drag a file / sample card here\n\nelonmusk\nmamdani\n@lindarsour"
                : "הכנס שמות משתמשים – שורה לכל אחד\nאו גרור קובץ / כרטיס דוגמה לכאן\n\nelonmusk\nnetanyahu\n@yairlapid"}
              value={text}
              onChange={e => setText(e.target.value)}
              disabled={loading}
              dir="ltr"
            />
            {isDragOver && (
              <div className="la-drop-overlay">
                <Upload size={28} color="#D89B5A" />
                <span>{mm ? "Drop to load" : "שחרר לטעינה"}</span>
              </div>
            )}
            {text && !loading && !isDragOver && (
              <button className="la-clear-btn" onClick={() => { setText(""); setStreamResults([]); setSummary(null); setErrors([]); }}>
                <X size={14} />
              </button>
            )}
          </div>

          <div className="la-actions">
            <div className="la-count-hint">
              {usernames.length > 0 && !loading && (
                <span>{usernames.length} {mm ? "users" : "משתמשים"}</span>
              )}
            </div>

            <button
              className="la-upload-btn"
              onClick={() => fileRef.current.click()}
              disabled={loading || fileLoading}
            >
              {fileLoading ? <div className="la-spinner la-spinner-sm" /> : <Upload size={14} />}
              <span>{fileLoading ? (mm ? "Reading..." : "קורא...") : (mm ? "Upload CSV / PDF" : "העלה CSV / PDF")}</span>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.txt,.pdf"
              style={{ display: "none" }}
              onChange={handleFile}
            />

            <button
              className="la-analyze-btn"
              onClick={handleAnalyze}
              disabled={loading || !usernames.length}
            >
              {loading ? (
                <>
                  <div className="la-spinner" />
                  <span>מנתח...</span>
                </>
              ) : (
                <>
                  <Search size={15} />
                  <span>{mm ? "Analyze List" : "נתח רשימה"}</span>
                </>
              )}
            </button>

            {/* כרטיס קובץ דוגמה */}
            <div
              className="la-sample-card"
              draggable
              onDragStart={e => e.dataTransfer.setData("text/plain", "sample")}
              title="גרור לאזור הניתוח"
            >
              <div className="la-sample-file-icon">
                <div className="la-sample-file-corner" />
                <span className="la-sample-file-ext">CSV</span>
              </div>
              <div className="la-sample-info">
                <span className="la-sample-name">{mm ? "Sample List" : "רשימה לדוגמה"}</span>
                <span className="la-sample-hint">{mm ? "→ Drag here" : "גרור לכאן ←"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        {loading && progress && (
          <div className="la-progress-wrap">
            <div className="la-progress-track">
              <div
                className="la-progress-fill"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div className="la-progress-info">
              {rateLimitWait ? (
                <span className="la-rate-limit">
                  <Clock size={11} />
                  {mm
                    ? `Rate limit reached — waiting ${rateLimitWait} seconds...`
                    : `מגיע ל-rate limit — ממתין ${rateLimitWait} שניות...`}
                </span>
              ) : (
                <span>
                  {mm ? "Analyzing" : "מנתח"} {progress.current} / {progress.total} {mm ? "users" : "משתמשים"}
                  &nbsp;·&nbsp; {progressPct}%
                  {streamResults.length > 0 && (
                    <span className="la-progress-bots">
                      &nbsp;·&nbsp; {streamResults.filter(r => r.is_bot).length} {mm ? "bots detected so far" : "בוטים זוהו עד כה"}
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Live results appear as they stream in */}
      {streamResults.length > 0 && (
        <>
          {/* Summary stats */}
          {activeSummary && (
            <div className="la-stats-row">
              <div className="la-stat-box">
                <span className="la-stat-val">{activeSummary.total}</span>
                <span className="la-stat-lbl">{mm ? "Analyzed" : "נותחו"}</span>
              </div>
              <div className="la-stat-box la-stat-bot">
                <span className="la-stat-val">{activeSummary.bots}</span>
                <span className="la-stat-lbl">{mm ? "Bots" : "בוטים"}</span>
              </div>
              <div className="la-stat-box la-stat-suspicious">
                <span className="la-stat-val">{activeSummary.suspicious}</span>
                <span className="la-stat-lbl">{mm ? "Suspicious" : "חשודים"}</span>
              </div>
              <div className="la-stat-box la-stat-human">
                <span className="la-stat-val">{activeSummary.humans}</span>
                <span className="la-stat-lbl">{mm ? "Human" : "אנושיים"}</span>
              </div>
              {activeSummary.errors > 0 && (
                <div className="la-stat-box la-stat-err">
                  <span className="la-stat-val">{activeSummary.errors}</span>
                  <span className="la-stat-lbl">{mm ? "Errors" : "שגיאות"}</span>
                </div>
              )}
            </div>
          )}

          {/* Filter tabs + export */}
          <div className="la-filter-row">
            <div className="la-filters">
              {[
                { key: "all",        label: mm ? `All (${streamResults.length})` : `הכל (${streamResults.length})` },
                { key: "bots",       label: mm ? `Bots (${streamResults.filter(r=>r.is_bot).length})` : `בוטים (${streamResults.filter(r=>r.is_bot).length})` },
                { key: "suspicious", label: mm ? `Suspicious (${streamResults.filter(r=>r.bot_type==="SUSPICIOUS").length})` : `חשודים (${streamResults.filter(r=>r.bot_type==="SUSPICIOUS").length})` },
                { key: "human",      label: mm ? `Human (${streamResults.filter(r=>r.bot_type==="HUMAN").length})` : `אנושיים (${streamResults.filter(r=>r.bot_type==="HUMAN").length})` },
              ].map(f => (
                <button
                  key={f.key}
                  className={`la-filter-btn ${filter === f.key ? "active" : ""}`}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {!loading && (
              <button className="la-export-btn" onClick={handleExport}>
                <Download size={13} />
                <span>{mm ? "Export CSV" : "ייצוא CSV"}</span>
              </button>
            )}
          </div>

          {/* Table */}
          <div className="la-table-wrap">
            <table className="la-table">
              <thead>
                <tr>
                  <th className="la-th">#</th>
                  <th className="la-th la-th-left">{mm ? "User" : "משתמש"}</th>
                  <th className="la-th">{mm ? "Followers" : "עוקבים"}</th>
                  <th className="la-th">{mm ? "Following" : "עוקב"}</th>
                  <th className="la-th">{mm ? "Age" : "גיל"}</th>
                  <th className="la-th">{mm ? "Tweets" : "ציוצים"}</th>
                  <th className="la-th">{mm ? "Score" : "ציון"}</th>
                  <th className="la-th">{mm ? "Type" : "סיווג"}</th>
                  <th className="la-th" />
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="la-empty">{mm ? "No results for this filter" : "אין תוצאות לסינון זה"}</td>
                  </tr>
                ) : (
                  filtered.map((r, i) => (
                    <ResultRow key={r.username} result={r} index={i} />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div className="la-errors">
              <div className="la-errors-title">
                <AlertTriangle size={14} color="#C8B455" />
                <span>{mm ? `Users not found (${errors.length})` : `משתמשים שלא נמצאו (${errors.length})`}</span>
              </div>
              <div className="la-errors-list">
                {errors.map((e, i) => (
                  <span key={i} className="la-error-chip">@{e.username}</span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
